import dayjs, { type OpUnitType, type QUnitType } from "dayjs";
import { ScheduleDataObjectType, type TimestampSchedule } from "./types.js";

/**
 * Creates a ScheduleIterator from a ScheduleDataObject object.
 * @param schedule
 * @returns A ScheduleIterator instance or undefined if the schedule is invalid.
 */
export function scheduleIterator(schedule: ScheduleDataObjectType) {
  if (!schedule) {
    console.error("Invalid schedule object provided:", schedule);
    return undefined;
  }

  if (schedule.schedule_type === "timestamp")
    return ScheduleIterator.create(schedule.timestamp);

  return undefined;
}
/**
 * ScheduleIterator is an iterable iterator that generates dates based on a schedule.
 * It supports iterating through scheduled dates and checking if a specific date matches any of the scheduled dates.
 * It does not manipulate the original schedule object, but rather creates a new iterator instance for each iteration.
 *
 * @example
 * ```typescript
 * // Create a schedule for every 2 weeks starting January 1st
 * const schedule = ScheduleIterator.create({number: 2, unit: "weeks", startDate: new Date("2025-01-01")});
 *
 * // Iterate through all dates
 * for (const date of schedule) {
 *   console.log(date);
 * }
 *
 * // Check if a specific date matches
 * const matches = schedule.matchesDate(new Date("2025-01-15")); // true
 *
 * // Get next scheduled date from now
 * const nextDate = schedule.nextDate();
 * ```
 */
export class ScheduleIterator implements IterableIterator<Date> {
  private currentDate: dayjs.Dayjs;
  private currentIteration = 0;
  private done = false;

  private readonly number: number;
  private readonly unit: "days" | "weeks" | "months" | "years";

  private readonly start: dayjs.Dayjs;
  private readonly end?: dayjs.Dayjs;

  /**
   * Creates a new ScheduleIterator from simple parameters.
   * @param number - The interval number (e.g., 2 for "every 2 months")
   * @param unit - The time unit ("days", "weeks", "months", "years")
   * @param startDate - Optional start date (defaults to current date)
   * @param endDate - Optional end date
   * @returns A new ScheduleIterator instance
   */
  static create(
    schedule: TimestampSchedule["timestamp"]
  ): ScheduleIterator | undefined {
    if (!schedule) {
      console.error("Invalid schedule object provided:", schedule);
      return undefined;
    }

    const { startDate, endDate, number, unit } = schedule;

    // validate the schedule parameters
    if (number <= 0) {
      console.error("Schedule number must be positive:", schedule);
      return undefined;
    }

    if (!unit || !["days", "weeks", "months", "years"].includes(unit)) {
      console.error("Invalid schedule unit provided:", schedule);
      return undefined;
    }

    const start = dayjs(startDate || new Date());
    const end = endDate ? dayjs(endDate) : undefined;

    if (end && end.isBefore(start)) {
      console.error("End date must be after start date:", schedule);
      return undefined;
    }

    return new ScheduleIterator(schedule);
  }

  /**
   * Creates a new ScheduleIterator from a TimestampSchedule object.
   * This constructor validates the schedule object and initializes the iterator.
   * If no schedule is provided, it will return undefined.
   * @param schedule - The TimestampSchedule object containing the schedule parameters.
   * @returns A new ScheduleIterator instance or undefined if the schedule is invalid.
   */
  private constructor(schedule: TimestampSchedule["timestamp"]) {
    const { startDate, endDate, number, unit } = schedule;

    this.start = dayjs(startDate || new Date());
    this.end = endDate ? dayjs(endDate) : undefined;
    this.currentDate = this.start;
    this.number = number;
    this.unit = unit;
    this.currentIteration = 0;
  }

  /**
   * Gets the next date in the schedule. Also checks if the end date is reached.
   * if no start date is given, it will use the current date.
   * @returns
   */
  next(): IteratorResult<Date> {
    this.currentIteration++;

    if (this.done) return { done: true, value: undefined };

    const value = this.currentDate.toDate();

    if (this.end && this.currentDate.isAfter(this.end)) {
      this.done = true;
      return { done: true, value: undefined };
    }

    // we calculate the next date based on the number and unit and the current iteration
    // we do so to keep the initial start date as anchor for all iterations (otherwise the day of month could shift)
    this.currentDate = this.start.add(
      this.number * this.currentIteration,
      this.unit
    );

    if (this.end && this.currentDate.isAfter(this.end)) this.done = true;

    return { done: false, value };
  }

  /**
   * Checks if any date matches a scheduled date (by day).
   * Generates actual scheduled dates to ensure accuracy with months/years.
   * @param date - The date to check against the schedule.
   * @returns true if the date matches any scheduled date, false otherwise.
   */
  matchesDate(date: Date): boolean {
    const targetDate = dayjs(date).startOf("day");
    const nextDate = this.nextDate(targetDate.toDate());
    const lastDate = this.lastDate(targetDate.toDate());
    return (
      dayjs(nextDate).isSame(targetDate, "day") ||
      dayjs(lastDate).isSame(targetDate, "day")
    );
  }

  /**
   * Calculates the time in milliseconds until the next scheduled date.
   * @param date - The date from which to calculate the next scheduled date. If not provided, it uses the current date.
   * @param unit - The unit of time to return the difference in. Defaults to "millisecond".
   * @returns The time in milliseconds until the next scheduled date, or -1 if no next date is found.
   */
  timeToNextDate(date?: Date, unit?: QUnitType | OpUnitType): number {
    const fromDate = date ?? new Date();
    const nextD = this.nextDate(fromDate);
    if (!nextD) return -1;

    return dayjs(nextD).diff(fromDate, unit ?? "millisecond", true);
  }

  /**
   * Gets the next scheduled date based on the current date or a provided start date.
   * Uses mathematical calculation for better performance.
   * @param start - The date from which to calculate the next scheduled date. If not provided, it uses the current date.
   * @returns The next scheduled date, or undefined if no next date is found.
   */
  nextDate(start?: Date): Date | undefined {
    const fromDate = dayjs(start ?? new Date());

    // If we're before the schedule starts, return the start date
    if (fromDate.isBefore(this.start)) {
      return this.start.toDate();
    }

    // Calculate how many intervals have passed since start
    const diffInUnits = fromDate.diff(this.start, this.unit, true);
    const nextInterval = Math.ceil(diffInUnits / this.number);

    // Calculate the next scheduled date
    const nextDate = this.start.add(nextInterval * this.number, this.unit);

    // Check if it's within bounds
    if (this.end && nextDate.isAfter(this.end)) {
      return undefined;
    }

    return nextDate.toDate();
  }

  /**
   * Gets the last scheduled date before a given start date.
   * Uses mathematical calculation for better performance.
   * If no start date is provided, it uses the current date.
   * @param start - The date from which to calculate the last scheduled date. If not provided, it uses the current date.
   * @returns The last scheduled date before the given start date, or undefined if no such date exists.
   */
  lastDate(start?: Date): Date | undefined {
    const fromDate = dayjs(start ?? new Date());

    // If we're before the schedule starts, no last date exists
    if (fromDate.isBefore(this.start)) {
      return undefined;
    }

    // Calculate how many intervals have passed since start
    const diffInUnits = fromDate.diff(this.start, this.unit, true);
    const lastInterval = Math.floor(diffInUnits / this.number);

    // Calculate the last scheduled date
    const lastDate = this.start.add(lastInterval * this.number, this.unit);

    // If the calculated date is not before the fromDate, go back one interval
    if (!lastDate.isBefore(fromDate)) {
      const previousDate = this.start.add(
        (lastInterval - 1) * this.number,
        this.unit
      );
      return previousDate.isBefore(this.start)
        ? undefined
        : previousDate.toDate();
    }

    return lastDate.toDate();
  }

  /**
   * Calculates the time from a given date to the last scheduled date.
   * @param date - The date from which to calculate the last scheduled date. If not provided, it uses the current date.
   * @param unit - The unit of time to return the difference in. Defaults to "millisecond".
   * @returns The time from the given date to the last scheduled date, or -1 if no last date is found.
   */
  timeToLastDate(date?: Date, unit?: QUnitType | OpUnitType): number {
    const fromDate = date ?? new Date();
    const lastD = this.lastDate(fromDate);
    if (!lastD) return -1;
    return dayjs(fromDate).diff(lastD, unit ?? "millisecond", true);
  }

  /**
   * Returns a string representation of the schedule.
   * @returns A human-readable description of the schedule
   */
  toString(): string {
    const endStr = this.end ? ` until ${this.end.format("YYYY-MM-DD")}` : "";
    return `Every ${this.number} ${this.unit} starting ${this.start.format("YYYY-MM-DD")}${endStr}`;
  }

  [Symbol.iterator](): IterableIterator<Date> {
    this.currentDate = this.start;
    this.done = false;
    this.currentIteration = 0;

    return this;
  }
}
